import { computed, inject, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'

import {
  AuthTokens,
  AuthUser,
  ConfirmResetPasswordInput,
  ConfirmSignUpInput,
  FetchUserAttributesOutput,
  ResetPasswordInput,
  resendSignUpCode,
  SignInInput,
  SignInWithRedirectInput,
  SignUpInput,
  UpdatePasswordInput,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  getCurrentUser,
  resetPassword,
  setUpTOTP,
  signIn,
  signInWithRedirect,
  signOut as authSignOut,
  signUp,
  updateMFAPreference,
  updatePassword,
  verifyTOTPSetup,
  ResendSignUpCodeInput,
} from 'aws-amplify/auth'

import { WSInstance } from '@/services/ws/utils/types'
import { UserConnectedAccount } from './utils/types'
import { isCredentialError } from './utils/helpers'

import {
  CUSTOM_ALLPOSIT_UUID_KEY,
  CUSTOM_PASSWORD_PROVIDED,
  REDIRECT_AVOID_ROUTES,
} from './utils/const'
import { ROUTE_NAME } from '@/const'

import { prepareResponseError } from '@/store/utils/helpers'
import { handleCatchedError } from '@/helpers/common'

import { useCapacitor } from '@/plugins/capacitor'
import { useNotifications } from '@/plugins/notification'
import { useHubspot } from '@/plugins/hubspot'
import { useChargebee } from '@/plugins/chargebee'

import faro from '@/services/faro'

import { useMainStore } from '@/store/main'
import { useUserSettingsStore } from './settings'
import { useUserSubscriptionsStore } from './subscriptions'
import api from '../api'

export const useUserStore = defineStore('user', () => {
  const mainStore = useMainStore()
  const userSettingsStore = useUserSettingsStore()
  const userSubscriptionsStore = useUserSubscriptionsStore()

  const capacitor = useCapacitor()
  const chargebee = useChargebee()

  const { clearAll, error, success } = useNotifications()

  const route = useRoute()
  const router = useRouter()

  const { VITE_APP_HUBSPOT_SCRIPT_ID } = import.meta.env

  // INIT

  const user = ref<AuthUser>()
  const tokens = ref<AuthTokens>()
  const attributes = ref<FetchUserAttributesOutput>()

  const isMFAEnabled = ref(false)
  const isPasswordProvided = ref(false)

  const loading = ref(false)

  const earlyAccessRequested = useLocalStorage<boolean>(
    'earlyAccessRequested',
    false,
  )

  const ws = inject<WSInstance>('ws')
  const hubspotScriptId = inject<string>('hubspotScriptId')

  const hubspot = useHubspot(hubspotScriptId)

  // GETTERS

  const isAuthenticated = computed(() => !!getUserId.value)

  const isEarlyAccessRequested = computed(() => earlyAccessRequested.value)

  const getUserId = computed(() => user.value?.userId)
  const getIdToken = computed(() => tokens.value?.idToken?.toString())
  const getAccessToken = computed(() => tokens.value?.accessToken?.toString())

  const getEmail = computed(() => attributes.value?.email)

  const getConnectedAccounts = computed<UserConnectedAccount[]>(() =>
    JSON.parse(attributes.value?.identities || '[]'),
  )

  const getAllpositUuid = computed(
    () => attributes.value?.[CUSTOM_ALLPOSIT_UUID_KEY],
  )

  // get params if query already has a path
  const getRedirectParams = computed(() => {
    const path = route.query.path as string
    if (!checkPath(path)) return {}
    return {
      query: {
        path,
      },
      hash: route.hash,
    }
  })

  // SETTERS

  const setUser = async (data: AuthUser) => {
    const session = await fetchAuthSession()

    user.value = data
    tokens.value = session.tokens

    await userSubscriptionsStore.fetchCurrentSubscription()

    // Init attributes
    attributes.value = await fetchUserAttributes()
    isPasswordProvided.value =
      attributes.value?.[CUSTOM_PASSWORD_PROVIDED] === 'true'

    // Init Hubspot
    ;(async function () {
      if (!VITE_APP_HUBSPOT_SCRIPT_ID) return

      const hubspotToken = (await api.get('/hubspot')).data.token

      if (getEmail.value && hubspotToken) {
        hubspot?.init(getEmail.value, hubspotToken)
      }
    }())

    // WS
    await connectToWS()
    const mfa = await fetchMFAPreference()
    isMFAEnabled.value = !!mfa.enabled?.length

    // Init Faro
    getAndValidateUuid().then(allpositUuid => {
      if (!allpositUuid) return
      faro?.initUser(allpositUuid)
    })
  }

  const setPasswordProvided = () => {
    isPasswordProvided.value = true
  }

  const setEarlyAccessRequested = () => {
    earlyAccessRequested.value = true
  }

  // ACTIONS

  const auth = async () => {
    if (isAuthenticated.value) return
    try {
      const user = await getCurrentUser()
      if (user.userId) {
        await setUser(user)
      } else {
        signOut()
      }
    } catch {
      // console.error(e)
    }
  }

  const connectToWS = async () => {
    if (!getIdToken.value) return
    await ws?.connect(getIdToken.value)
  }

  const checkPath = (path: string) => {
    if (typeof path !== 'string' || path.trim().length === 0) {
      return false
    }
    const resolved = router.resolve(path)
    return resolved.matched.length > 0 && !REDIRECT_AVOID_ROUTES.includes(resolved.name as string)
  }

  const redirectToRoute = async () => {
    const path = route.query.path as string
    if (path) {
      if (checkPath(path)) {
        await router.push({ path, hash: route.hash })
      } else {
        await router.push({ name: ROUTE_NAME.DASHBOARDS })
      }
    } else {
      await router.push({ name: ROUTE_NAME.DASHBOARDS })
    }
  }

  const signUpEmail = async (data: SignUpInput) => {
    loading.value = true
    try {
      const { isSignUpComplete, userId, nextStep } = await signUp(data)
      if (!isSignUpComplete && nextStep.signUpStep !== 'CONFIRM_SIGN_UP')
        throw 'Unknown user'
      return userId
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Sign up error',
        description,
      })
    } finally {
      loading.value = false
    }
  }

  const signUpConfirm = async (data: ConfirmSignUpInput) => {
    loading.value = true
    try {
      const { nextStep } = await confirmSignUp(data)
      return nextStep.signUpStep === 'DONE'
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Sign up confirm error',
        description,
      })
    } finally {
      loading.value = false
    }
  }

  const signInPassword = async (data: SignInInput) => {
    loading.value = true
    try {
      const { nextStep } = await signIn(data)
      data.password && capacitor?.setCredentials(data.username, data.password)
      if (nextStep.signInStep === 'DONE') {
        await clearAll()
        await redirectToRoute()
      } else {
        await router.push({ name: ROUTE_NAME.SIGNIN_CONFIRMATION, ...getRedirectParams.value })
      }
    } catch (e) {
      const errorMessage = prepareResponseError(e)
      handleCatchedError(errorMessage)

      if (isCredentialError(e)) {
        await error({
          message: 'Incorrect password or email',
          description:
            'Make sure you typed the email and password correctly and then try again',
        })
      } else {
        await error({
          message: 'Sign in error',
          description: errorMessage || 'An unexpected error occurred during sign in. Please try again.',
        })
      }
    } finally {
      loading.value = false
    }
  }

  const signInMFA = async (challengeResponse: string) => {
    loading.value = true
    try {
      const { nextStep } = await confirmSignIn({
        challengeResponse,
      })
      if (nextStep.signInStep !== 'DONE') throw 'Unknown user'
      isMFAEnabled.value = true
      await clearAll()
      await redirectToRoute()
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Signin confirm error',
        description,
      })
    } finally {
      loading.value = false
    }
  }

  const signInSocial = async (data: SignInWithRedirectInput) => {
    loading.value = true
    try {
      await signInWithRedirect(data)
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Signin error',
        description,
      })
    } finally {
      loading.value = false
    }
  }

  const signOut = async (checkBlockers = true) => {
    if (checkBlockers) {
      try {
        await mainStore.checkBlockers()
      } catch {
        return
      }
    }
    loading.value = true
    try {
      clear()
      chargebee.destroy()
      await mainStore.globalClear()
      await authSignOut()
      ws?.disconnect()
      hubspot?.destroy()
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Error during signout',
        description,
      })
    } finally {
      loading.value = false
    }
    router.push({ name: ROUTE_NAME.SIGNIN })
  }

  const forgotPassword = async (data: ResetPasswordInput) => {
    loading.value = true
    try {
      await resetPassword(data)
      return true
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Password reset error',
        description,
      })
      return false
    } finally {
      loading.value = false
    }
  }

  const resendSignUpConfirmationCode = async (data: ResendSignUpCodeInput) => {
    loading.value = true
    try {
      await resendSignUpCode(data)
      return true
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Error during sign up code resend',
        description,
      })
      return false
    } finally {
      loading.value = false
    }
  }

  const changePassword = async (data: ConfirmResetPasswordInput) => {
    loading.value = true
    try {
      await confirmResetPassword(data)
      await success({
        message: 'Password successfully changed',
      })
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Error during password change',
        description,
      })
      return false
    } finally {
      loading.value = false
    }
    return true
  }

  const newPassword = async (data: UpdatePasswordInput) => {
    loading.value = true
    try {
      await updatePassword(data)
      await success({
        message: 'Password successfully changed',
      })
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Error during password change',
        description,
      })
    } finally {
      loading.value = false
    }
  }

  const refreshToken = async (): Promise<AuthTokens | undefined> => {
    const session = await fetchAuthSession({ forceRefresh: true })
    if (session.tokens && session.userSub) {
      tokens.value = session.tokens
      await connectToWS()
      return session.tokens
    } else {
      await signOut(false)
      return undefined
    }
  }

  const setupTOTP = async () => {
    try {
      const output = await setUpTOTP()
      return output.sharedSecret
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Error during installation of two-factor authentication',
        description,
      })
    }
  }

  const verifyTOTP = async (code: string) => {
    loading.value = true
    try {
      await verifyTOTPSetup({ code })
      await updateMFAPreference({ totp: 'PREFERRED' })
      isMFAEnabled.value = true
      await success({
        message: 'You have successfully enabled Two-Factor Authentication',
      })
      return true
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Error setting verification code. Please try again',
        description,
      })
      return false
    } finally {
      loading.value = false
    }
  }

  const disableTOTP = async () => {
    try {
      await updateMFAPreference({ totp: 'DISABLED' })
      isMFAEnabled.value = false
      await success({
        message: 'You disabled Two-Factor Authentication',
      })
    } catch (e) {
      const description = prepareResponseError(e)
      handleCatchedError(description)
      await error({
        message: 'Error during deactivation of two-factor authentication',
        description,
      })
    }
  }

  const getAndValidateUuid = async () => {
    if (!getAllpositUuid.value) {
      await error({
        message: 'Settings fetch error',
        description:
          'Something went wrong, please contact support to resolve the problem',
      })
      await signOut(false)
      return undefined
    }
    return getAllpositUuid.value
  }

  watch(
    getEmail,
    async email => {
      if (!email || userSubscriptionsStore.isInactive) return

      await mainStore.fetchRepositoryData()
    },
    { immediate: true },
  )

  const clear = () => {
    user.value = undefined
    tokens.value = undefined
    attributes.value = undefined

    isMFAEnabled.value = false
    isPasswordProvided.value = false

    faro?.resetUser()
    userSettingsStore.clear()
    userSubscriptionsStore.clear()
  }

  return {
    isAuthenticated,

    isEarlyAccessRequested,

    loading,

    setUser,
    setPasswordProvided,
    setEarlyAccessRequested,

    getUserId,
    getIdToken,
    getAccessToken,
    getEmail,
    getAllpositUuid,
    getConnectedAccounts,

    getAndValidateUuid,

    getRedirectParams,

    isPasswordProvided,
    isMFAEnabled,

    auth,

    signUpEmail,
    signUpConfirm,

    signInPassword,
    signInMFA,
    signInSocial,

    signOut,

    forgotPassword,
    changePassword,
    newPassword,
    refreshToken,
    resendSignUpConfirmationCode,

    setupTOTP,
    verifyTOTP,
    disableTOTP,

    clear,
  }
})
